import { useItem, useList } from '../../api/queries'
import type { LubricationEvent, LubricationSpec, NextDue, Recommendation } from '../../api/types'
import { StatusBadge } from '../../components/ui'
import { DataTable, type Column } from '../../components/DataTable'
import { formatDate, formatLitres, humanize } from '../../lib/format'

export function nextDueText(n: Pick<NextDue, 'status' | 'reason' | 'remaining_hours' | 'due_at_hours'>): string {
  if (n.status === 'unknown') return `Desconocido: ${n.reason || 'faltan las horas de operación o el tipo de aceite'}`
  return n.remaining_hours !== null ? `${n.remaining_hours} h restantes (vence a las ${n.due_at_hours} h)` : humanize(n.status)
}

export const eventColumns: Column<LubricationEvent>[] = [
  { key: 'date', header: 'Realizado', cell: (r) => formatDate(r.performed_on) },
  { key: 'eq', header: 'Equipo', cell: (r) => <span className="mono">{r.equipment_code}</span> },
  { key: 'kind', header: 'Tipo', cell: (r) => humanize(r.kind) },
  { key: 'type', header: 'Aceite', cell: (r) => `${humanize(r.lubricant_type)}${r.iso_vg ? ` ISO VG ${r.iso_vg}` : ''}` },
  { key: 'qty', header: 'Cantidad', cell: (r) => formatLitres(r.quantity_liters), priority: 'low' },
  { key: 'hours', header: 'Horas', cell: (r) => r.operating_hours ?? 'Desconocido', priority: 'low' },
  { key: 'by', header: 'Por', cell: (r) => r.performed_by_name, priority: 'low' },
]

export function LubricationPanel({ equipmentId }: { equipmentId: number }) {
  const spec = useList<LubricationSpec>('/api/lubrication/specs/', { equipment: equipmentId })
  const recs = useList<Recommendation>('/api/lubrication/recommendations/', { equipment: equipmentId })
  const events = useList<LubricationEvent>('/api/lubrication/events/', { equipment: equipmentId })
  const due = useItem<NextDue>(`/api/lubrication/equipment/${equipmentId}/next-due/`)
  const s = spec.data?.results[0]
  return (
    <div className="stack">
      <dl className="defs">
        <dt>Lubricante recomendado</dt>
        <dd>{s ? `${humanize(s.lubricant_type)} ISO VG ${s.iso_vg}` : 'No hay especificación de lubricante registrada'}</dd>
        <dt>Próximo cambio de aceite</dt>
        <dd>{due.data ? <><StatusBadge tone={due.data.status === 'unknown' ? 'neutral' : 'info'} label={humanize(due.data.status)} /> {nextDueText(due.data)}</> : '—'}</dd>
        <dt>Recomendaciones del informe</dt>
        <dd>{recs.data?.results.length ? recs.data.results.map((r) => `${humanize(r.action)} (${humanize(r.status)})`).join(', ') : 'Ninguna'}</dd>
      </dl>
      <DataTable caption="Eventos de lubricación" rowKey={(r) => r.id} columns={eventColumns} rows={events.data?.results} loading={events.isPending} error={events.error} emptyTitle="No hay eventos de lubricación registrados" emptyHint="Lo recomendado no es lo realizado: los eventos aparecen aquí cuando alguien los registra." />
    </div>
  )
}
